import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "../components/Layout";
import api from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Input } from "../components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { MagnifyingGlass, DownloadSimple, EnvelopeSimple, Phone, ArrowRight } from "@phosphor-icons/react";

/**
 * Admin Leads Page — unified view of:
 *  1) Contact / Register Interest submissions (from /api/interest)
 *  2) Free-course self-signups (users with interest_type = "free_course")
 */
export default function AdminLeadsPage() {
  const [submissions, setSubmissions] = useState([]);
  const [freeUsers, setFreeUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => {
    Promise.all([
      api.get("/interest").then(r => r.data).catch(() => []),
      api.get("/users").then(r => r.data).catch(() => []),
    ]).then(([subs, users]) => {
      setSubmissions(subs || []);
      setFreeUsers((users || []).filter(u => u.interest_type === "free_course"));
      setLoading(false);
    });
  }, []);

  const filteredSubs = submissions.filter(s =>
    !q || s.name?.toLowerCase().includes(q.toLowerCase()) ||
    s.email?.toLowerCase().includes(q.toLowerCase()) ||
    s.programme_interest?.toLowerCase().includes(q.toLowerCase())
  );
  const filteredFree = freeUsers.filter(u =>
    !q || u.name?.toLowerCase().includes(q.toLowerCase()) ||
    u.email?.toLowerCase().includes(q.toLowerCase())
  );

  const exportCSV = (rows, headers, filename) => {
    const csv = [headers.map(h => h.label), ...rows.map(r => headers.map(h => r[h.key] || ""))]
      .map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Layout>
      <div className="p-6 md:p-8 lg:p-12" data-testid="admin-leads-page">
        <div className="mb-6 flex items-center justify-between flex-wrap gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1">Administration</p>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-950" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Leads & Submissions
            </h1>
            <p className="text-sm text-slate-500 mt-1">Contact-form submissions and free-course self-signups — all in one place.</p>
          </div>
          <div className="relative max-w-xs w-full">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Search name, email, programme..."
              value={q}
              onChange={e => setQ(e.target.value)}
              className="pl-9 rounded-none"
              data-testid="leads-search"
            />
          </div>
        </div>

        <Tabs defaultValue="contact">
          <TabsList className="rounded-none">
            <TabsTrigger value="contact" className="rounded-none" data-testid="tab-contact">
              Contact / Interest Forms <Badge className="ml-2 rounded-none bg-slate-200 text-slate-700 text-[10px]">{submissions.length}</Badge>
            </TabsTrigger>
            <TabsTrigger value="free" className="rounded-none" data-testid="tab-free">
              Free Course Signups <Badge className="ml-2 rounded-none bg-emerald-200 text-emerald-800 text-[10px]">{freeUsers.length}</Badge>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="contact" className="mt-4">
            <div className="flex justify-end mb-3">
              <Button
                variant="outline"
                className="rounded-none gap-1 text-xs"
                onClick={() => exportCSV(filteredSubs,
                  [
                    { key: "name", label: "Name" },
                    { key: "email", label: "Email" },
                    { key: "phone", label: "Phone" },
                    { key: "programme_interest", label: "Programme" },
                    { key: "message", label: "Message" },
                    { key: "created_at", label: "Submitted" },
                  ],
                  `contact-submissions-${new Date().toISOString().slice(0,10)}.csv`)}
                data-testid="export-submissions-csv"
              >
                <DownloadSimple size={13} /> Export CSV
              </Button>
            </div>
            {loading ? (
              <div className="h-48 bg-slate-100 animate-pulse" />
            ) : (
              <Card className="border border-slate-200 rounded-none shadow-sm overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs uppercase tracking-wider">Name</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider">Contact</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider">Programme Interest</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider">Message</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider">Submitted</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredSubs.length === 0 ? (
                      <TableRow><TableCell colSpan={5} className="text-center text-sm text-slate-400 py-10">No submissions yet</TableCell></TableRow>
                    ) : filteredSubs.map((s, i) => (
                      <TableRow key={s.id || i} data-testid={`submission-row-${i}`}>
                        <TableCell className="font-medium text-sm">{s.name || "—"}</TableCell>
                        <TableCell className="text-xs text-slate-600">
                          <div className="flex items-center gap-1"><EnvelopeSimple size={12} />{s.email || "—"}</div>
                          {s.phone && <div className="flex items-center gap-1 mt-0.5"><Phone size={12} />{s.phone}</div>}
                        </TableCell>
                        <TableCell className="text-xs">
                          {s.programme_interest ? <Badge className="rounded-none bg-blue-100 text-blue-800 text-[10px]">{s.programme_interest}</Badge> : <span className="text-slate-300">—</span>}
                        </TableCell>
                        <TableCell className="text-xs text-slate-600 max-w-sm truncate" title={s.message}>
                          {s.message || <span className="text-slate-300">—</span>}
                        </TableCell>
                        <TableCell className="text-xs text-slate-500 whitespace-nowrap">
                          {s.created_at ? new Date(s.created_at).toLocaleString() : "—"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="free" className="mt-4">
            <div className="flex justify-between mb-3 items-center">
              <p className="text-xs text-slate-500">
                Showing {filteredFree.length} users who signed up specifically for Free Courses.
              </p>
              <div className="flex gap-2">
                <Link to="/admin/users?interest=free_course">
                  <Button variant="outline" className="rounded-none gap-1 text-xs" data-testid="open-in-users-btn">
                    Open in User Management <ArrowRight size={12} />
                  </Button>
                </Link>
                <Button
                  variant="outline"
                  className="rounded-none gap-1 text-xs"
                  onClick={() => exportCSV(filteredFree,
                    [
                      { key: "name", label: "Name" },
                      { key: "email", label: "Email" },
                      { key: "phone", label: "Phone" },
                      { key: "school_name", label: "Employer/School" },
                      { key: "position", label: "Position" },
                      { key: "interest_choice", label: "Programme Interest" },
                      { key: "created_at", label: "Signed Up" },
                    ],
                    `free-signups-${new Date().toISOString().slice(0,10)}.csv`)}
                  data-testid="export-free-csv"
                >
                  <DownloadSimple size={13} /> Export CSV
                </Button>
              </div>
            </div>
            {loading ? (
              <div className="h-48 bg-slate-100 animate-pulse" />
            ) : (
              <Card className="border border-slate-200 rounded-none shadow-sm overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs uppercase tracking-wider">Name</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider">Email</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider">Phone</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider">Employer/School</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider">Position</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider">Programme Curious About</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider">Signed Up</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredFree.length === 0 ? (
                      <TableRow><TableCell colSpan={7} className="text-center text-sm text-slate-400 py-10">No free-course signups yet</TableCell></TableRow>
                    ) : filteredFree.map((u, i) => (
                      <TableRow key={u.id} data-testid={`free-user-row-${i}`}>
                        <TableCell className="font-medium text-sm">{u.name || "—"}</TableCell>
                        <TableCell className="text-xs text-slate-600">{u.email}</TableCell>
                        <TableCell className="text-xs text-slate-600">{u.phone || <span className="text-slate-300">—</span>}</TableCell>
                        <TableCell className="text-xs text-slate-600">{u.school_name || <span className="text-slate-300">—</span>}</TableCell>
                        <TableCell className="text-xs text-slate-600">{u.position || <span className="text-slate-300">—</span>}</TableCell>
                        <TableCell className="text-xs">
                          {u.interest_choice ? <Badge className="rounded-none bg-amber-100 text-amber-800 text-[10px]">{u.interest_choice}</Badge> : <span className="text-slate-300">—</span>}
                        </TableCell>
                        <TableCell className="text-xs text-slate-500 whitespace-nowrap">
                          {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
